import { ref } from 'vue';
import type { PatternTest } from '@record-fill';
import type { PatternRunner } from './pattern-runner';

export interface PatternGuard {
  patternTest: PatternTest;
  hasPending: () => boolean;
  settled: () => Promise<void>;
}

const MAX_CACHED_RESULTS = 500;

interface CachedResult {
  matches: boolean;
  readAt: number;
}

/**
 * compileMatrix is synchronous and the guarded test is not. An unknown pattern and text pair
 * passes for now and is checked in the background. Every answer that lands bumps a reactive
 * generation, so a computed that compiled with this test compiles again with the real answer.
 */
export function createPatternGuard(run: PatternRunner): PatternGuard {
  const generation = ref(0);
  const results = new Map<string, CachedResult>();
  const pending = new Map<string, Promise<void>>();
  let lastRead = 0;

  // Evicting by age alone looped forever once a compilation used more pairs than the bound: each
  // answer dropped a pair still in use, the recompilation asked for it again, and settled() never
  // returned. Only pairs the latest compilation did not read may go; the cache grows otherwise.
  // An answer that just landed counts as read at its landing, so a burst of answers is never
  // dropped before the compilation they trigger has read them.
  function trim(): void {
    for (const [key, cached] of results) {
      if (results.size <= MAX_CACHED_RESULTS) return;
      if (cached.readAt < lastRead) results.delete(key);
    }
  }

  function remember(key: string, matches: boolean): void {
    results.set(key, { matches, readAt: generation.value });
    trim();
    generation.value += 1;
  }

  const patternTest: PatternTest = (pattern, text) => {
    const current = generation.value;
    lastRead = current;
    const key = `${pattern.length}:${pattern}${text}`;
    const cached = results.get(key);
    if (cached) {
      cached.readAt = current;
      return cached.matches;
    }

    if (!pending.has(key)) {
      const job = run(pattern, text)
        .catch(() => true)
        .then((matches) => {
          remember(key, matches);
          pending.delete(key);
        });
      pending.set(key, job);
    }
    return true;
  };

  async function settled(): Promise<void> {
    while (pending.size > 0) {
      await Promise.all([...pending.values()]);
    }
  }

  return { patternTest, hasPending: () => pending.size > 0, settled };
}
